import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Building, Users, Server, Activity, Cloud, RefreshCw, Shield, Globe } from 'lucide-react';
import StatusBadge from '../../components/common/StatusBadge';
import Tabs, { type Tab } from '../../components/common/Tabs';
import TenantModal from '../../components/admin/TenantModal';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import type { Column } from '../../components/common/DataTable';
import { tenantService, systemService, vpnUserService, appDeviceService, edgeService, mtgeService, connectorService, userService } from '../../services';
import type { Tenant } from '../../services';
import { useToast } from '../../components/common/ToastContainer';
import TenantAddressSpace from '../../components/tenants/TenantAddressSpace';
import { formatDate } from '../../utils/formatDate';
import { appDeviceProtocolLabel } from '../../utils/appDeviceProtocol';
import { useSettingsStore } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';
import StatCard from '../../components/common/StatCard';
import tenantSettingsService from '../../services/tenantSettings.service';

interface ExtendedTenant extends Tenant {
  appUsers?: number;
  devices?: number;
  admins?: number;
  edges?: number;
  mtges?: number;
  connectors?: number;
}

const TenantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [tenant, setTenant] = useState<ExtendedTenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gvsaCredentials, setGvsaCredentials] = useState<any>(null);
  const [gvsaStatus, setGvsaStatus] = useState<any>(null);
  const [registering, setRegistering] = useState(false);
  const [maxVpnDevicesPerUser, setMaxVpnDevicesPerUser] = useState<number>(5);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        // Global VSA is visible to whoever can manage the tenant. Both reads
        // work for a ChannelAdmin on their own channel's tenant: the credentials
        // endpoint is not SuperAdmin-gated (it returns only whether creds exist,
        // never the secret), and getGlobalVsaStatus is scope-checked via
        // getTenantById. So fetch for everyone; each has its own fallback.
        const [tenantData, credsData, statusData] = await Promise.all([
          tenantService.getById(id),
          systemService.getGlobalVsaCredentials().catch(() => ({ configured: false })),
          tenantService.getGlobalVsaStatus(id).catch(() => ({ registered: false, status: 'not_registered' })),
        ]);
        setTenant(tenantData);
        setGvsaCredentials(credsData);
        setGvsaStatus(statusData);
      } catch (err: any) {
        setError(err.message || 'Failed to load tenant details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSaveTenant = async (updatedTenant: any) => {
    if (!id) return;
    try {
      await tenantService.update(id, {
        name: updatedTenant.name,
        description: updatedTenant.description,
      });

      // SuperAdmin: persist max_vpn_devices_per_user if changed
      if (isSuperAdmin && updatedTenant.maxVpnDevicesPerUser !== undefined &&
          updatedTenant.maxVpnDevicesPerUser !== maxVpnDevicesPerUser) {
        await tenantSettingsService.updateCategory(id, 'security', {
          max_vpn_devices_per_user: updatedTenant.maxVpnDevicesPerUser,
        });
        setMaxVpnDevicesPerUser(updatedTenant.maxVpnDevicesPerUser);
      }

      const tenantData = await tenantService.getById(id);
      setTenant(tenantData);
      setIsEditModalOpen(false);
      toast.success('Tenant Updated', 'Tenant information has been updated successfully');
    } catch (err: any) {
      toast.error('Update Failed', err.message || 'Failed to update tenant');
    }
  };

  const handleRegisterClick = () => {
    if (!gvsaCredentials?.configured) {
      toast.warning('Configuration Required', 'Global VSA credentials are not configured. Please configure them in System Settings first.');
      return;
    }
    setShowRegisterDialog(true);
  };

  const handleRegisterWithGlobalVSA = async () => {
    if (!id) return;

    try {
      setRegistering(true);
      setShowRegisterDialog(false);

      // Get the orchestrator URL from config or use a default
      const orchestratorUrl = gvsaCredentials.orchestratorUrl || window.location.origin;

      await tenantService.registerWithGlobalVsa(id, { orchestratorUrl });

      // Refresh status
      const statusData = await tenantService.getGlobalVsaStatus(id);
      setGvsaStatus(statusData);

      toast.success('Registration Successful', 'Tenant registered with Global VSA. Waiting for administrator approval.');
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to register with Global VSA';
      toast.error('Registration Failed', errorMsg);
    } finally {
      setRegistering(false);
    }
  };

  const handleOpenEditModal = async () => {
    if (isSuperAdmin && id) {
      try {
        const cat = await tenantSettingsService.getCategory(id, 'security');
        setMaxVpnDevicesPerUser(cat.settings?.max_vpn_devices_per_user ?? 5);
      } catch {
        // non-fatal: leave default
      }
    }
    setIsEditModalOpen(true);
  };

  const tenantForModal = tenant ? {
    id: tenant.id,
    name: tenant.name,
    description: tenant.description || '',
    maxVpnDevicesPerUser,
  } : undefined;

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading tenant details...</div>;
  }

  if (error || !tenant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'Tenant not found'}</div>
      </div>
    );
  }

  // Tab 1: Overview
  const OverviewTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tenant Information</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Tenant Name</label>
              <p className="text-gray-900">{tenant.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Description</label>
              <p className="text-gray-900">{tenant.description || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Shown to whoever can manage the tenant (SuperAdmin or the owning
            ChannelAdmin) — both can read the status and register. */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-600" />
            Global VSA Registration
          </h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <div className="flex items-center gap-2 mt-1">
                {gvsaStatus?.registered ? (
                  <>
                    <StatusBadge
                      status={gvsaStatus.status === 'active' ? 'active' :
                        gvsaStatus.status === 'pending_approval' ? 'pending' :
                          gvsaStatus.status === 'suspended' ? 'inactive' : 'error'}
                    />
                    <span className="text-sm text-gray-600">{gvsaStatus.message}</span>
                  </>
                ) : (
                  <>
                    <StatusBadge status="inactive" />
                    <span className="text-sm text-gray-600">Not registered</span>
                  </>
                )}
              </div>
            </div>
            {gvsaStatus?.registeredAt && (
              <div>
                <label className="text-sm font-medium text-gray-500">Registered At</label>
                <p className="text-gray-900">{formatDate(new Date(gvsaStatus.registeredAt), dateSettings)}</p>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timestamps</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Created</label>
              <p className="text-gray-900">{tenant.createTime || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-gray-900">{tenant.updateTime || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const TopMetricsSection = () => (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
      <StatCard title="VPN Users" value={tenant.appUsers || 0} icon={Users} color="info" />
      <StatCard title="Devices" value={tenant.devices || 0} icon={Server} color="secondary" />
      <StatCard title="Admins" value={tenant.admins || 0} icon={Users} color="primary" />
      <StatCard title="Edges" value={tenant.edges || 0} icon={Server} color="success" />
      <StatCard title="MTGEs" value={tenant.mtges || 0} icon={Cloud} color="warning" />
      <StatCard title="Connectors" value={tenant.connectors || 0} icon={Activity} color="info" />
    </div>
  );

  const UsersTab = ({ tenantId }: { tenantId: string }) => {
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      vpnUserService.list({ tenantId }).then((res: any) => setUsers(res.items)).finally(() => setLoading(false));
    }, [tenantId]);

    const columns: Column<any>[] = [
      {
        key: 'email',
        label: 'Email',
        sortable: true,
        render: (u) => (
          <button
            onClick={() => navigate(`/admin/vpn-users/${u.id}`)}
            className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
          >
            <Users className="w-4 h-4 text-primary-600" />
            <span className="font-medium">{u.email}</span>
          </button>
        ),
      },
      { key: 'displayName', label: 'Name', sortable: true, render: (u) => <span>{u.displayName || u.email?.split('@')[0] || '—'}</span> },
      {
        key: 'deviceGroupName',
        label: 'Device Group',
        sortable: true,
        render: (u) => {
          const names: string[] = u.deviceGroupNames || [];
          if (names.length === 0) return <span className="text-gray-400">—</span>;
          if (names.length === 1) return <span>{names[0]}</span>;
          return (
            <div className="flex items-center space-x-1">
              <span className="font-medium">{names[0]}</span>
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">+{names.length - 1}</span>
            </div>
          );
        },
      },
      { key: 'status', label: 'Status', sortable: true, render: (u) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${u.status === 0 ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{u.status === 0 ? 'Active' : 'Disabled'}</span> },
      { key: 'connectionCount', label: 'Connections', sortable: true },
    ];

    return <DataTable columns={columns} data={users} loading={loading} searchPlaceholder="Search users..." />;
  };

  const DevicesTab = ({ tenantId }: { tenantId: string }) => {
    const [devices, setDevices] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      appDeviceService.listAllDevices({ tenantId }).then((res: any) => setDevices(res.items)).finally(() => setLoading(false));
    }, [tenantId]);

    const columns: Column<any>[] = [
      {
        key: 'name',
        label: 'Device Name',
        sortable: true,
        render: (d) => (
          <button
            onClick={() => navigate(`/app-devices/${d.id}`)}
            className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
          >
            <Server className="w-4 h-4 text-primary-600" />
            <span className="font-medium">{d.name}</span>
          </button>
        ),
      },
      { key: 'platform', label: 'Platform', sortable: true },
      { key: 'tunnelProtocol', label: 'Protocol', sortable: true, render: (d) => <span className="font-mono text-xs">{appDeviceProtocolLabel(d)}</span> },
      { key: 'status', label: 'Status', sortable: true, render: (d) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${d.status === 'active' ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{d.status}</span> },
    ];

    return <DataTable columns={columns} data={devices} loading={loading} searchPlaceholder="Search devices..." />;
  };

  const EdgesTab = ({ tenantId }: { tenantId: string }) => {
    const [edges, setEdges] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      edgeService.list({ tenantId }).then((res: any) => setEdges(res.items)).finally(() => setLoading(false));
    }, [tenantId]);

    const columns: Column<any>[] = [
      {
        key: 'devName',
        label: 'Edge Name',
        sortable: true,
        render: (e) => (
          <button
            onClick={() => navigate(`/edges/${e.devSerNum}`)}
            className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
          >
            <Server className="w-4 h-4 text-primary-600" />
            <span className="font-medium">{e.devName}</span>
          </button>
        ),
      },
      { key: 'devSerNum', label: 'Serial Number', sortable: true },
      { key: 'vppMode', label: 'Datapath', sortable: true, render: (e) => <span className="font-mono text-xs">{(e as any).vppMode || 'DPDK'}</span> },
      { key: 'upInformation', label: 'Status', sortable: true, render: (e) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${e.upInformation ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{e.upInformation ? 'Online' : 'Offline'}</span> },
    ];

    return <DataTable columns={columns} data={edges} loading={loading} searchPlaceholder="Search edges..." />;
  };

  const MTGEsTab = ({ tenantId }: { tenantId: string }) => {
    const [mtges, setMtges] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      mtgeService.list({ tenantId }).then((res: any) => setMtges(res.items)).finally(() => setLoading(false));
    }, [tenantId]);

    const columns: Column<any>[] = [
      {
        key: 'device_name',
        label: 'MTGE Name',
        sortable: true,
        render: (m) => (
          <button
            onClick={() => navigate(`/mtges/${m.id}`)}
            className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
          >
            <Cloud className="w-4 h-4 text-primary-600" />
            <span className="font-medium">{m.device_name}</span>
          </button>
        ),
      },
      { key: 'serial_number', label: 'Serial', sortable: true },
      { key: 'wan_ip', label: 'WAN IP', sortable: true },
      { key: 'status', label: 'Status', sortable: true, render: (m) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${m.status === 'active' ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{m.status}</span> },
    ];

    return <DataTable columns={columns} data={mtges} loading={loading} searchPlaceholder="Search MTGEs..." />;
  };

  const ConnectorsTab = ({ tenantId }: { tenantId: string }) => {
    const [connectors, setConnectors] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      connectorService.list({ tenantId }).then((res: any) => setConnectors(res.items)).finally(() => setLoading(false));
    }, [tenantId]);

    const columns: Column<any>[] = [
      {
        key: 'connector_name',
        label: 'Connector Name',
        sortable: true,
        render: (c) => (
          <button
            onClick={() => navigate(`/connectors/${c.id}`)}
            className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
          >
            <Activity className="w-4 h-4 text-primary-600" />
            <span className="font-medium">{c.connector_name}</span>
          </button>
        ),
      },
      { key: 'serial_number', label: 'Serial', sortable: true },
      { key: 'wan_ip', label: 'WAN IP', sortable: true },
      { key: 'status', label: 'Status', sortable: true, render: (c) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.status === 'active' ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{c.status}</span> },
    ];

    return <DataTable columns={columns} data={connectors} loading={loading} searchPlaceholder="Search connectors..." />;
  };

  const AdminsTab = ({ tenantId }: { tenantId: string }) => {
    const [admins, setAdmins] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      userService.list({ tenantId, type: USER_TYPES.TENANT_ADMIN }).then((res: any) => setAdmins(res.items)).finally(() => setLoading(false));
    }, [tenantId]);

    const columns: Column<any>[] = [
      {
        key: 'email',
        label: 'Email',
        sortable: true,
        render: (a) => (
          <button
            onClick={() => navigate(`/admin/users/${a.id}`)}
            className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
          >
            <Shield className="w-4 h-4 text-primary-600" />
            <span className="font-medium">{a.email || a.username}</span>
          </button>
        ),
      },
      { key: 'userRealName', label: 'Name', sortable: true, render: (a) => <span>{a.userRealName || '—'}</span> },
      { key: 'status', label: 'Status', sortable: true, render: (a) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${a.status === 0 ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{a.status === 0 ? 'Active' : 'Disabled'}</span> },
      { key: 'createTime', label: 'Created', sortable: true },
    ];

    return <DataTable columns={columns} data={admins} loading={loading} searchPlaceholder="Search admins..." />;
  };

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview', icon: Building, content: <OverviewTab key="overview" /> },
    { id: 'app-users', label: 'VPN Users', icon: Users, content: <UsersTab tenantId={tenant.id} key="users" /> },
    { id: 'devices', label: 'Devices', icon: Server, content: <DevicesTab tenantId={tenant.id} key="devices" /> },
    { id: 'edges', label: 'Edges', icon: Server, content: <EdgesTab tenantId={tenant.id} key="edges" /> },
    // MTGEs are Shared Gateways — a SuperAdmin-only domain the backend does not
    // expose to a ChannelAdmin, so the tab would only ever 403 for them.
    ...(isSuperAdmin ? [{ id: 'mtges', label: 'MTGEs', icon: Cloud, content: <MTGEsTab tenantId={tenant.id} key="mtges" /> }] : []),
    { id: 'connectors', label: 'Connectors', icon: Activity, content: <ConnectorsTab tenantId={tenant.id} key="connectors" /> },
    { id: 'address-space', label: 'Address Space', icon: Globe, content: <TenantAddressSpace tenantId={tenant.id} key="address-space" /> },
    ...(isSuperAdmin ? [{ id: 'admins', label: 'Admins', icon: Shield, content: <AdminsTab tenantId={tenant.id} key="admins" /> }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{tenant.name}</h1>
          </div>
          <StatusBadge status="Active" />
        </div>
        <div className="flex items-center space-x-3">
          {/* Shown when the platform has Global VSA credentials configured.
              A ChannelAdmin may register their own channel's tenants (the
              backend scope-checks the tenant); the system credentials are used
              server-side and never exposed here. */}
          {gvsaCredentials?.configured && (
            <button
              onClick={handleRegisterClick}
              disabled={registering}
              className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
              title={!gvsaCredentials?.configured ? 'Configure Global VSA credentials in System Settings first' : 'Register this tenant with Global VSA'}
            >
              {registering ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Cloud className="w-4 h-4" />
              )}
              <span>Register with Global VSA</span>
            </button>
          )}
          <button onClick={handleOpenEditModal} className="btn-primary inline-flex items-center space-x-2">
            <Edit className="w-4 h-4" />
            <span>Edit Info</span>
          </button>
        </div>
      </div>

      <TopMetricsSection />

      {/* Tabs */}
      <div className="card">
        <Tabs tabs={tabs} />
      </div>

      {/* Edit Tenant Modal */}
      {tenantForModal && (
        <TenantModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveTenant}
          tenant={tenantForModal}
          mode="edit"
        />
      )}

      {/* Register Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        onConfirm={handleRegisterWithGlobalVSA}
        title="Register with Global VSA"
        message={`Register tenant "${tenant?.name}" with Global VSA?\n\nThis will allow edge devices for this tenant to be discovered by the Global VSA service.`}
        confirmText="Register Tenant"
        cancelText="Cancel"
        variant="info"
        isLoading={registering}
      />
    </div>
  );
};

export default TenantDetail;
